import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { NgxSafePipesModule } from "@it-era/ngx-safe-pipes";
import {
  FaStackComponent,
  FaIconComponent,
  FaStackItemSizeDirective,
} from "@fortawesome/angular-fontawesome";
import { Observable } from "rxjs";
import { ToastrService } from "@iqx-limited/ngx-toastr";

import { CatalogueSelectors } from "src/app/core/store/selectors";
import { CoreActions } from "src/app/core/store/actions";
import { VIEW } from "src/app/core/enums/view";
import { Seo } from "src/app/core/services/seo.service";
import { PricePipe } from "src/app/core/pipes/price.pipe";
import { environment } from "src/environments/environment";
import { ArrayService } from "src/app/core/services/array.service";
import {
  BreadcrumbService,
  BreadcrumbItem,
} from "src/app/core/services/breadcrumb.service";
import { UrlPipe } from "src/app/core/pipes/url.pipe";
import { Category } from "../../models/category.model";
import { Product } from "../../models/product.model";
import { toSignal } from "@angular/core/rxjs-interop";
import { ViewTransitionService } from "src/app/core/services/view-transition.service";

@Component({
  selector: "audio-category",
  templateUrl: "./category.component.html",
  styleUrls: ["./category.component.sass"],
  imports: [
    FaStackComponent,
    FaIconComponent,
    FaStackItemSizeDirective,
    RouterLink,
    NgxSafePipesModule,
    PricePipe,
    UrlPipe,
  ],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryComponent {
  private store = inject(Store);
  private toastr = inject(ToastrService);
  private viewTransitionService = inject(ViewTransitionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private seo = inject(Seo);
  private breadcrumbService = inject(BreadcrumbService);

  environment = environment;

  $routeUrl = toSignal(this.route.url);
  $routeQueryParamMap = toSignal(this.route.queryParamMap);
  $routeParamMap = toSignal(this.route.paramMap);

  $url = computed(() =>
    "/" + (this.$routeUrl()?.map((segment) => segment.path).join("/") ?? ""),
  );

  public $searchValue = signal("");
  public $showPacks = signal(true);

  categories$: Observable<Category[]> = this.store.pipe(
    select(CatalogueSelectors.getCategories),
  );
  $categories: Signal<Category[]> = toSignal(this.categories$, { initialValue: [] });
  $category: Signal<Category | undefined> = computed(() => {
    const categorySlug = this.$routeParamMap()?.get("category");

    if (categorySlug?.length) {
      return this.flattenCategories(this.$categories() ?? []).filter(
        (category) => category.slug === categorySlug,
      )[0];
    } else {
      return this.$categories()[0];
    }
  });

  $products = computed(() =>
    ArrayService.unique(
      this.flattenCategories(this.$category() ? [this.$category()!] : []).flatMap(
        (category) => category.products,
      ),
      "id",
    )
      .filter((product) => product.isBase)
      .filter(
        (product) =>
          product.status_sell || (!product.status_sell && product.status_buy),
    )
      .sort((a: Product, b: Product) => {
        // const bVariantsQty = b.variants?.length;
        // const aVariantsQty = a.variants?.length;
        const bItemsQty = b.items?.reduce((acc, item) => (acc += item.qty ?? 0), 0);
        const aItemsQty = a.items?.reduce((acc, item) => (acc += item.qty ?? 0), 0);
        const bPrice = +b.price;
        const aPrice = +a.price;
        return bItemsQty * 100 - aItemsQty * 100 + bPrice - aPrice;
        // bPrice === 0 || aPrice === 0
        //   ? bItemsQty - aItemsQty
        //   : bPrice - aPrice;
      })
      .map((product) => ({
        ...product,
        documents: product.documents
          .filter((document) => {
            if (!document["content-type"]) return false;
            switch (document["content-type"]) {
              case "image/png":
              case "image/jpeg":
              case "image/webp":
                return true;
              default:
                return false;
            }
          })
          .sort((a, b) => (a.date < b.date ? -1 : 1)),
      })),
  );

  $filteredProducts = signal<Product[]>([]);
  $breadcrumbItems: WritableSignal<BreadcrumbItem[]> = signal([]);

  VIEW = VIEW;
  $view = signal(VIEW.CELLS_LARGE);

  constructor() {
    console.log(
      this.$routeUrl(),
      this.$routeQueryParamMap(),
      this.$routeParamMap(),
      this.$category(),
    );

    effect(() => {
      if (
        this.$searchValue()?.trim()?.toLowerCase() !==
        this.$routeQueryParamMap()?.get("search")
      ) {
        this.$searchValue.set(this.$routeQueryParamMap()?.get("search") || "");
      }
    });

    effect(() => {
      if (!this.$category()) {
        const categorySlug = this.$routeParamMap()?.get("category");

        this.toastr.error(
          "La catégorie '" + categorySlug + "' est introuvable.",
        );
        this.store.dispatch(new CoreActions.Go({ path: ["/location"] }));
      }
    });

    effect(() => {
      if (this.$category()) {
        this.store.dispatch(new CoreActions.SetBreadcrumb(this.$category()!));
      }
      this.seo.setMeta({ property: "og:type", content: "website" });
      this.seo.setTitle(
        this.$category()?.label.toUpperCase() + " | " + environment.title,
      );
      this.seo.setDescription(this.$category()?.description);
      this.seo.setMeta({ property: "product:price:amount", content: "0" });

      this.$breadcrumbItems.set(
        this.breadcrumbService.getItems(this.$category()),
      );
    });

    effect(() => {
      this.search(this.$searchValue(), this.$products());
    });
  }

  search(value: string, products = this.$products()): void {
    this.$searchValue.set(value);
    const term = value?.trim().toLowerCase();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: term ? { search: term } : {},
    });

    const terms = term
      .split(/ (?=(?:(?:[^"]*"){2})*[^"]*$)/)
      .filter((t) => t.length);

    this.$filteredProducts.set(
      (
        terms.length
        ? products.filter((product) =>
            terms.some((t) => {
              const term = t.replaceAll('"', "");
              return (
                product.brand?.toLowerCase().includes(term) ||
                product.label.toLowerCase().includes(term) ||
                product.description.toLowerCase().includes(term) ||
                product.items?.find(
                  (item) =>
                    item.brand?.toLowerCase().includes(term) ||
                    item.label.toLowerCase().includes(term) ||
                    item.description.toLowerCase().includes(term),
                )
              );
            }),
          )
        : products).filter(
          (product) => this.$showPacks() ? true : !product.items?.length
        ),
    );
  }

  emptySearch(): void {
    this.search("");
  }

  goToTopCategory(): void {
    const term = this.$searchValue()?.trim().toLowerCase();

    this.router.navigate(["/location"], {
      queryParams: term ? { search: term } : {},
    });
  }

  setListView(): void {
    this.viewTransitionService.run(() => {
      this.$view.set(VIEW.LIST);
    });
  }

  setCellsLargeView(): void {
    this.viewTransitionService.run(() => {
      this.$view.set(VIEW.CELLS_LARGE);
    });
  }

  setCellsSmallView(): void {
    this.viewTransitionService.run(() => {
      this.$view.set(VIEW.CELLS_SMALL);
    });
  }

  toggleShowPacks(): void {
    this.viewTransitionService.run(() => {
      this.$showPacks.set(!this.$showPacks());
    });
  }

  flattenCategories(categories: Category[]): Category[] {
    return categories.flatMap((category) => [
      category,
      ...this.flattenCategories(category.children),
    ]);
  }
}
